import { useEffect } from 'react';
import { driver } from 'driver.js';
import { useAuth } from '../contexts/AuthContext';

export function useTour() {
  const { user } = useAuth();

  const startTour = () => {
    const driverObj = driver({
      showProgress: true,
      animate: true,
      smoothScroll: true,
      allowClose: true,
      nextBtnText: 'Next →',
      prevBtnText: '← Back',
      doneBtnText: 'Done 🚀',
      progressText: '{{current}} of {{total}}',
      popoverClass: 'lynqio-tour-popover',
      onDestroyed: () => {
        if (user) {
          localStorage.setItem(`tour_completed_${user.id}`, 'true');
        }
      },
      steps: [
        {
          popover: {
            title: 'Welcome to Lynqio 👋',
            description:
              "Your LinkedIn outreach command centre. Let's take a 60-second tour so you know exactly where everything lives.",
          },
        },
        {
          element: '[data-tour="runs"]',
          popover: {
            title: 'Runs — Where the magic starts',
            description:
              'Paste a LinkedIn job URL and Lynqio finds the top decision-makers, writes personalised messages, and queues outreach campaigns — all automatically.',
            side: 'right',
            align: 'center',
          },
        },
        {
          element: '[data-tour="pipeline"]',
          popover: {
            title: 'Pipeline',
            description:
              'Drag-and-drop kanban board. Track every prospect from the first connection request all the way to signed mandate.',
            side: 'right',
            align: 'center',
          },
        },
        {
          element: '[data-tour="jobs"]',
          popover: {
            title: 'Jobs',
            description:
              'Browse and claim open job orders. Claiming a job prevents duplicate outreach across your whole team.',
            side: 'right',
            align: 'center',
          },
        },
        {
          element: '[data-tour="contacts"]',
          popover: {
            title: 'Contacts',
            description:
              'Every prospect, candidate, and client in one searchable database. Filter by company, role, or outreach status.',
            side: 'right',
            align: 'center',
          },
        },
        {
          element: '[data-tour="tasks"]',
          popover: {
            title: 'Tasks',
            description:
              'Stay on top of follow-ups and action items. High-priority tasks surface at the top so nothing falls through the cracks.',
            side: 'right',
            align: 'center',
          },
        },
        {
          element: '[data-tour="messages"]',
          popover: {
            title: 'Team Chat',
            description:
              'Slack-style messaging built right in. Channels, DMs, threads, emoji reactions, and real-time search — no extra tool needed.',
            side: 'right',
            align: 'center',
          },
        },
        {
          element: '[data-tour="team-tools"]',
          popover: {
            title: 'Team Tools',
            description:
              'Performance dashboards, daily standups, target account lists, and company blacklists — everything for a coordinated team.',
            side: 'right',
            align: 'start',
          },
        },
        {
          element: '[data-tour="user-menu"]',
          popover: {
            title: "You're all set! 🚀",
            description:
              'Connect HeyReach and Apollo in Settings → Integrations to unlock full automation. Settings, Billing, Integrations, and Help now live here, under your profile.',
            side: 'right',
            align: 'center',
          },
        },
      ],
    });

    driverObj.drive();
  };

  useEffect(() => {
    if (!user?.id) return;
    if (localStorage.getItem(`tour_completed_${user.id}`) === 'true') return;

    const timer = setTimeout(startTour, 900);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return { startTour };
}
